import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/site/Sections";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { api, API_BASE } from "../lib/api";

export const formatDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const PostCard = ({ p, i }) => (
  <Reveal delay={(i % 3) * 0.08}>
    <Link to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
      <div className="aspect-[3/2] overflow-hidden rounded-[1.25rem] bg-muted">
        <img src={p.cover_image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
      </div>
      <div className="mt-5 flex items-center gap-3 text-xs font-medium text-ink/65">
        <span className="rounded-full bg-flame/10 px-2.5 py-1 font-semibold text-flame-dark">{p.category}</span>
        <span>{formatDate(p.created_at)}</span><span>·</span><span>{p.read_time}</span>
      </div>
      <h2 className="mt-3 font-display text-xl font-semibold leading-snug tracking-tight text-ink transition-colors group-hover:text-flame sm:text-2xl">{p.title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-ink/70">{p.excerpt}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ink">Read article <ArrowUpRight className="h-4 w-4 text-flame" /></span>
    </Link>
  </Reveal>
);

export default function Blog() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [data, setData] = useState({ posts: [], categories: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!API_BASE) { setLoading(false); return undefined; }
    const t = setTimeout(() => {
      api.get("/posts", { params: { q: q || undefined, category } })
        .then((r) => setData({ posts: Array.isArray(r.data?.posts) ? r.data.posts : [], categories: Array.isArray(r.data?.categories) ? r.data.categories : [] }))
        .catch(() => setData((d) => ({ ...d, posts: [] })))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, category]);

  const cats = ["All", ...new Set([...data.categories, ...(category !== "All" ? [category] : [])])];

  return (
    <>
      <Seo path="/blog" title="Marketing blog" description="Practical, no-jargon marketing advice for Canberra businesses on SEO, content, social, email and paid ads." />
      <PageHeader eyebrow="Blog" lines={["Ideas worth", "acting on."]} intro="Practical marketing advice for busy business owners. Short enough to read over coffee, useful enough to act on before it goes cold." />
      <section className="wrap pb-20 sm:pb-28">
        <div className="flex flex-col gap-5 border-b border-ink/10 pb-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Categories">
            {cats.map((c) => (
              <button key={c} onClick={() => setCategory(c)} role="tab" aria-selected={category === c} data-testid={`blog-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c ? "bg-ink text-canvas" : "border border-ink/15 text-ink hover:border-ink"}`}>
                {c}
              </button>
            ))}
          </div>
          <label className="relative block w-full lg:w-80">
            <span className="sr-only">Search articles</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" data-testid="blog-search-input"
              className="w-full rounded-full border border-ink/15 bg-white py-3 pl-11 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-ink/45 focus:border-ink" />
          </label>
        </div>
        {loading ? (
          <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="aspect-[3/2] animate-pulse rounded-[1.25rem] bg-muted" />)}</div>
        ) : data.posts.length === 0 ? (
          <p className="mt-16 text-center text-ink/65" data-testid="blog-empty">Nothing here yet. Try another word, or a different category.</p>
        ) : (
          <div className="mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {data.posts.map((p, i) => <PostCard key={p.id} p={p} i={i} />)}
          </div>
        )}
      </section>
    </>
  );
}
